import { Layout } from '@/bloks/Global/Layout';
import { getServerPageConfig } from '@/server/config';
import { getBreadcrumbs } from '@/server/config/getBreadcrumbs';
import { DefaultProps, buildGetStaticProps } from '@/server/slug';
import { GetStaticPaths } from 'next';
import { TeamtailorSingleJobProvider } from '../../../../contexts/teamtailor/TeamtailorProvider';
import {
	Department,
	JobAdvertisement,
	JobAdvertisementInitialState,
	JobLocation,
} from '../../../../contexts/teamtailor/types';
import { fetchAllDepartments, fetchAllLocations, fetchJobById } from '../../../../server/teamtailor/instance';

// Component based on Storyblok blog post example
// https://github.com/storyblok/nextjs-multilanguage-website/blob/main/pages/%5B%5B...slug%5D%5D.js

interface Props extends DefaultProps {
	job: JobAdvertisement | null;
	locations: JobLocation[];
	departments: Department[];
	jobId?: string | null;
}

const Page: React.FC<Props> = ({ job, locations, departments, ...props }) => {
	return (
		<TeamtailorSingleJobProvider job={job ?? JobAdvertisementInitialState} locations={locations}>
			<Layout {...props} />
		</TeamtailorSingleJobProvider>
	);
};

export default Page;

export const getStaticProps = buildGetStaticProps<Props>({
	initialize(params) {
		const jobId = params.slug ? params.slug[0] : '';
		const storySlug = 'karriar/lediga-jobb/jobb';
		const pageSlug = jobId ? `karriar/lediga-jobb/jobb/${jobId}` : 'karriar/lediga-jobb/jobb';

		return { build: { pageSlug, storySlug, jobId } };
	},
	async build(helper) {
		const { jobId } = helper;

		await helper.fetchProps({
			job: () => fetchJobById(jobId),
			locations: () => fetchAllLocations(),
			departments: () => fetchAllDepartments(),
		});

		const { story, job } = helper.result!.props;
		const breadcrumbs = await getBreadcrumbs('karriar/lediga-jobb', story.name, helper.sbParams);

		if (!job || !jobId) {
			return {
				redirect: {
					permanent: false,
					destination: '/karriar/lediga-jobb',
				},
			};
		}

		const canonicalPath = `${story.full_slug}/${job.id}`;
		const dynamicMetaTitle = `${job.title} | Fortnox`;
		const dynamicMetaDescription = job.pitch ?? '';
		const image = { filename: job.picture ?? undefined, id: Number(job.id) };

		breadcrumbs.push({ item: `/karriar/lediga-jobb/jobb/${jobId}`, name: job.title });

		helper.result!.props.config = getServerPageConfig({
			slug: helper.storySlug,
			story,
			breadcrumbs,
			image,
		});

		helper.result!.props.dynamicMeta = {
			title: dynamicMetaTitle,
			description: dynamicMetaDescription,
			canonical: canonicalPath,
		};
	},
});

export const getStaticPaths: GetStaticPaths = async () => {
	return { paths: [], fallback: 'blocking' };
};
